import { useEffect, useLayoutEffect, type ReactElement } from "react";
import { RouterProvider } from "react-router-dom";
import { AccountQueryProvider } from "./lib/queryClient";
import { router } from "./routes";
import { useThemeStore } from "./core/stores/useThemeStore";
import { useAuthStore } from "./core/stores/useAuthStore";
import { ToastHost } from "./core/components/ToastHost";
import { ErrorBoundary } from "./core/components/ErrorBoundary";

export function App(): ReactElement {
  const mode = useThemeStore((s) => s.mode);
  const hydrate = useAuthStore((s) => s.hydrate);
  const accountId = useAuthStore((s) => s.user?.id ?? "signed-out");

  // Before the first paint, so a dark preference never flashes light.
  useLayoutEffect(() => {
    document.documentElement.classList.toggle("dark", mode === "dark");
  }, [mode]);

  useEffect(() => {
    void hydrate();
  }, [hydrate]);

  useEffect(() => {
    const keepNumberOnScroll = (event: WheelEvent): void => {
      const input = event.target;
      if (
        input instanceof HTMLInputElement &&
        input.type === "number" &&
        input === document.activeElement
      ) {
        // Blur before the browser steps the number; leave native page/dialog scrolling alone.
        input.blur();
      }
    };
    // Capture also covers portalled dialogs.
    document.addEventListener("wheel", keepNumberOnScroll, { capture: true, passive: true });
    return () => {
      document.removeEventListener("wheel", keepNumberOnScroll, true);
    };
  }, []);

  return (
    <>
      {import.meta.env.MODE === "sandbox" ? (
        <div
          role="status"
          className="pointer-events-none fixed top-1 right-2 z-[2000] rounded-sm bg-orange-400 px-2 py-0.5 text-[11px] font-bold text-black"
        >
          LOCAL TEST
        </div>
      ) : null}
      <AccountQueryProvider key={accountId}>
        <ErrorBoundary>
          <RouterProvider router={router} />
          <ToastHost />
        </ErrorBoundary>
      </AccountQueryProvider>
    </>
  );
}
